import type { Range } from "@otrack/domain";

export const MINUTE = 60_000;
export const HOUR = 3_600_000;

/** Local midnight of the day containing `ts`, shifted by `dayOffset` days. */
export function startOfDay(ts: number, dayOffset = 0): number {
  const date = new Date(ts);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + dayOffset);
  return date.getTime();
}

export function dayRange(ts: number, dayOffset = 0): Range {
  return { from: startOfDay(ts, dayOffset), to: startOfDay(ts, dayOffset + 1) };
}

/** Monday-based week containing `ts`. */
export function weekRange(ts: number): Range {
  const date = new Date(startOfDay(ts));
  const shift = (date.getDay() + 6) % 7;
  const from = startOfDay(date.getTime(), -shift);
  return { from, to: startOfDay(from, 7) };
}

export function monthRange(ts: number): Range {
  const date = new Date(ts);
  return { from: new Date(date.getFullYear(), date.getMonth(), 1).getTime(), to: new Date(date.getFullYear(), date.getMonth() + 1, 1).getTime() };
}

const pad = (value: number) => String(value).padStart(2, "0");

export function formatClock(ts: number): string {
  const date = new Date(ts);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * "1h 25m", "45m", "2h"; "42s" under a minute. Rounds down, so it never shows time not yet tracked.
 * With `seconds`, a running count keeps its seconds: "1m 59s", "2h 0m 7s".
 */
export function formatDuration(ms: number, { seconds = false }: { seconds?: boolean } = {}): string {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const hours = Math.floor(total / 3600), minutes = Math.floor(total / 60) % 60, rest = total % 60;
  if (seconds) return hours ? `${hours}h ${minutes}m ${rest}s` : minutes ? `${minutes}m ${rest}s` : `${rest}s`;
  if (!hours && !minutes && rest) return `${rest}s`;
  if (!hours) return `${minutes}m`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/** "1:32:07" */
export function formatStopwatch(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 3600)}:${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}`;
}

export function formatDay(ts: number): string {
  return new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" }).format(ts);
}

const shortDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

/** "Oct 22, 2026" */
export function formatShortDate(ts: number): string {
  return shortDay.format(ts);
}

/** "Oct 1 - 31, 2026" for two days, both included. */
export function formatPeriod(from: number, to: number): string {
  if (from === to) return shortDay.format(from);
  return shortDay.formatRange(from, to).replace(/\s*[\u2013\u2014]\s*/g, " - ");
}

/** The previous calendar month as its first and last day: the period a monthly invoice covers. */
export function lastMonth(now: number): { from: number; to: number } {
  const date = new Date(now);
  return { from: new Date(date.getFullYear(), date.getMonth() - 1, 1).getTime(), to: new Date(date.getFullYear(), date.getMonth(), 0).getTime() };
}

export function formatMonth(ts: number): string {
  return new Intl.DateTimeFormat("en-US", { month: "long" }).format(ts);
}

/** Value for an <input type="time">. */
export function toTimeInput(ts: number): string {
  return formatClock(ts);
}

/** Applies an "HH:MM" value to the day containing `dayTs`. */
export function fromTimeInput(dayTs: number, value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  return startOfDay(dayTs) + Number(match[1]) * HOUR + Number(match[2]) * MINUTE;
}

/**
 * Time fields show whole minutes, so a parsed value that still falls on `exact`'s minute means
 * the field was left alone: keep `exact`, seconds and all. Rounding a Timer started right as
 * another stopped back to the minute would push it into the other entry.
 */
export function keepExact(parsed: number | null, exact: number | null): number | null {
  if (parsed === null || exact === null) return parsed;
  return parsed === new Date(exact).setSeconds(0, 0) ? exact : parsed;
}

const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short" });
const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

/** How long ago `ts` was, coarser as it recedes: "just now", "25 min ago", "3h ago", "yesterday", "on Tue", "on Oct 12". */
export function formatAgo(ts: number, now: number): string {
  const minutes = Math.floor((now - ts) / MINUTE);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const days = Math.round((startOfDay(now) - startOfDay(ts)) / 86_400_000);
  if (days === 0) return `${Math.floor(minutes / 60)}h ago`;
  if (days === 1) return "yesterday";
  return `on ${(days < 7 ? weekday : monthDay).format(ts)}`;
}

/** "" for today, "yesterday ", or "Mon, Oct 19 " for other days. */
export function dayPrefix(ts: number, now: number): string {
  const days = Math.round((startOfDay(now) - startOfDay(ts)) / 86_400_000);
  if (days === 0) return "";
  if (days === 1) return "yesterday ";
  return `${new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" }).format(ts)} `;
}
